import { useCallback, useEffect, useState } from "react";
import signalsApi, { isConfigured } from "../../services/signalsApi";
import { normalizeReport, normalizeReportHistory } from "./reportModel";

/**
 * useEngineReport — read-only fetch of the engine's GET /report and
 * GET /report/history.
 *
 * signalsApi.js is HUMAN-ONLY, so both client functions ship as proposed
 * patches a human applies after review:
 *   getReport()        → patches/signalsApi-getReport.patch
 *   getReportHistory() → patches/signalsApi-getReportHistory.patch
 * Until then this hook FEATURE-DETECTS each one: a missing function on the
 * service ⇒ that slice reports itself unavailable and the UI shows an honest
 * "not connected" / "building history" state. Nothing is mocked in the
 * meantime.
 *
 * Moves no money and changes no execution — display only.
 */
export const reportSupported = () => typeof signalsApi.getReport === "function";
export const historySupported = () => typeof signalsApi.getReportHistory === "function";

export default function useEngineReport() {
  const available = isConfigured() && reportSupported();
  const historyAvailable = isConfigured() && historySupported();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(available);
  const [error, setError] = useState(null);
  const [historyError, setHistoryError] = useState(null);

  const refresh = useCallback(async () => {
    if (!available && !historyAvailable) return;
    setLoading(true);
    const [rep, hist] = await Promise.allSettled([
      available ? signalsApi.getReport() : Promise.resolve(null),
      historyAvailable ? signalsApi.getReportHistory() : Promise.resolve(null),
    ]);

    // 404 before enrollment is expected (same convention as /status):
    // there's just no report yet, not an error worth alarming anyone with.
    if (rep.status === "fulfilled") {
      setReport(rep.value === null ? null : normalizeReport(rep.value));
      setError(null);
    } else if (rep.reason?.code === "HTTP_404") {
      setReport(null);
      setError(null);
    } else {
      setError(rep.reason?.message || String(rep.reason));
    }

    // History failing must not take down the rest of the section — the chart
    // card surfaces its own error and everything else keeps rendering.
    if (hist.status === "fulfilled") {
      setHistory(hist.value === null ? null : normalizeReportHistory(hist.value));
      setHistoryError(null);
    } else if (hist.reason?.code === "HTTP_404") {
      setHistory(null);
      setHistoryError(null);
    } else {
      setHistory(null);
      setHistoryError(hist.reason?.message || String(hist.reason));
    }

    setLoading(false);
  }, [available, historyAvailable]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { available, historyAvailable, report, history, historyError, loading, error, refresh };
}
